// src/views/AccommodationProvider/ProviderProperties.js
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Badge, 
  Row, 
  Col, 
  Button, 
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Alert,
  Spinner
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const ProviderProperties = () => {
  const navigate = useNavigate();
  
  // Get provider information from localStorage (set during login)
  const providerType = localStorage.getItem('provider_type');
  const providerName = localStorage.getItem('provider_name');
  const representativeName = localStorage.getItem('representative_name');
  const providerId = localStorage.getItem('provider_id');
  const regStatus = localStorage.getItem('reg_status');

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });

  // Determine welcome message based on provider type
  const getWelcomeMessage = () => {
    if (providerType === 'individual') {
      return `My Properties - ${providerName || 'Individual Provider'}`;
    } else if (providerType === 'entity') {
      return `My Properties - ${providerName || 'Your Organization'}`;
    } else {
      return 'My Properties';
    }
  };

  const getSubtitleMessage = () => {
    if (providerType === 'entity' && representativeName) {
      return `Representative: ${representativeName}`;
    }
    return null;
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  // Fetch all properties for this provider (no status filter)
  useEffect(() => {
    const fetchProperties = async () => {
      if (!providerId || regStatus !== '2') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axiosInstance.get(`/api/properties/provider/${providerId}/`);
        
        if (response.status === 200) {
          setProperties(response.data.properties || []);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
        setAlert({
          type: 'danger',
          message: 'Failed to load properties. Please try again.',
          visible: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [providerId, regStatus]);

  const handleBackToPropertyManagement = () => {
    navigate('/provider-property-management');
  };

  const handleConfigureBlocks = (propertyId) => {
    navigate(`/provider-dashboard/configure-blocks/${propertyId}`);
  };

  const handleConfigureFloors = (propertyId) => {
    // TODO: Implement configure floors functionality
    console.log(`Configure Floors for property ${propertyId}`);
    // navigate(`/provider-dashboard/configure-floors/${propertyId}`);
  };

  const handleConfigureRoomType = (propertyId) => {
    navigate(`/provider-dashboard/create-room-type/${propertyId}`);
  };

  const handleManageProperty = (propertyId) => {
    // TODO: Implement manage property functionality
    console.log(`Manage property ${propertyId}`);
    navigate(`/provider-dashboard/manage-property/${propertyId}`);
  };

  const formatAddress = (property) => {
    const parts = [
      property.addresslines,
      property.suburb,
      property.city,
      property.province
    ].filter(Boolean);
    return parts.join(', ');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'created':
        return <Badge color="secondary">Created</Badge>;
      case 'inspection_requested':
        return <Badge color="warning">Inspection Requested</Badge>;
      case 'inspection_booked':
        return <Badge color="info">Inspection Booked</Badge>;
      case 'inspection_failed':
        return <Badge color="danger">Inspection Failed</Badge>;
      case 'inspection_passed':
        return <Badge color="success">Inspection Passed</Badge>;
      case 'active':
        return <Badge color="success">Active</Badge>;
      case 'inactive':
        return <Badge color="secondary">Inactive</Badge>;
      case 'suspended':
        return <Badge color="danger">Suspended</Badge>;
      default:
        return <Badge color="light">{status}</Badge>;
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Standard Header with Add Button */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">My Properties</h2>
        <Button 
          color="primary" 
          onClick={() => navigate('/provider-add-property')}
          className="d-flex align-items-center"
        >
          <i className="fas fa-plus me-2"></i>
          ADD +
        </Button>
      </div>

      {/* Provider Info Section */}
      <div className="mb-3">
        <Badge color="info" className="me-2">
          {getProviderTypeDisplay()}
        </Badge>
        {getSubtitleMessage() && (
          <span className="text-muted">{getSubtitleMessage()}</span>
        )}
      </div>

      {/* Alert */}
      {alert.visible && (
        <Row className="mb-3">
          <Col>
            <Alert 
              color={alert.type} 
              toggle={() => setAlert({ ...alert, visible: false })}
            >
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Properties Table */}
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-building me-2 text-primary"></i>
                All My Properties
              </CardTitle>
              
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-3 text-muted">Loading properties...</p>
                </div>
              ) : (
                <>
                  {properties.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="bi bi-building-slash display-4 text-muted"></i>
                      <h5 className="mt-3 text-muted">No Properties Found</h5>
                      <p className="text-muted">
                        {regStatus !== '2' 
                          ? "Complete your registration to add properties."
                          : "You don't have any properties yet. Start by adding your first property."
                        }
                      </p>
                      <Button 
                        color="primary" 
                        onClick={() => navigate('/provider-add-property')}
                        disabled={regStatus !== '2'}
                      >
                        <i className="bi bi-plus-lg me-2"></i>
                        Add New Property
                      </Button>
                    </div>
                  ) : (
                    <div className="table-responsive" style={{ paddingBottom: '150px' }}>
                      <Table hover>
                        <thead>
                          <tr>
                            <th>Property Name</th>
                            <th>Address</th>
                            <th>Rooms</th>
                            <th>Beds</th>
                            <th>Status</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {properties.map((property) => (
                            <tr key={property.id}>
                              <td>
                                <div className="fw-bold">
                                  {property.trading_name || `Property #${property.id}`}
                                </div>
                              </td>
                              <td>
                                <div>{formatAddress(property)}</div>
                                <small className="text-muted">
                                  {property.postcode}
                                </small>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-light text-dark">
                                  {property.number_of_rooms || 0}
                                </span>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-light text-dark">
                                  {property.number_of_beds || 0}
                                </span>
                              </td>
                              <td>
                                {getStatusBadge(property.status)}
                              </td>
                              <td className="text-center">
                                <UncontrolledDropdown>
                                  <DropdownToggle 
                                    color="light" 
                                    size="sm" 
                                    className="btn-outline-secondary"
                                    caret={false}
                                  >
                                    <i className="bi bi-three-dots"></i>
                                  </DropdownToggle>
                                  <DropdownMenu end>
                                    <DropdownItem 
                                      onClick={() => handleConfigureBlocks(property.id)}
                                    >
                                      <i className="bi bi-building me-2"></i>
                                      Configure Blocks
                                    </DropdownItem>
                                    <DropdownItem 
                                      onClick={() => handleConfigureRoomType(property.id)}
                                    >
                                      <i className="bi bi-gear me-2"></i>
                                      Configure Room Type
                                    </DropdownItem>
                                    <DropdownItem 
                                      onClick={() => handleManageProperty(property.id)}
                                    >
                                      <i className="bi bi-sliders me-2"></i>
                                      Manage Property
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Action Summary */}
      {properties.length > 0 && (
        <Row className="mt-4">
          <Col md="12">
            <Card className="border-0 bg-light">
              <CardBody>
                <CardTitle tag="h6" className="mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  Property Management Actions
                </CardTitle>
                <Row>
                  <Col md="4">
                    <div className="mb-2">
                      <strong>Configure Blocks:</strong>
                    </div>
                    <ul className="small text-muted">
                      <li>Manage building blocks</li>
                      <li>Add or edit blocks</li>
                      <li>Organize property structure</li>
                    </ul>
                  </Col>
                  <Col md="4">
                    <div className="mb-2">
                      <strong>Configure Room Type:</strong>
                    </div>
                    <ul className="small text-muted">
                      <li>Set room categories and types</li>
                      <li>Define room specifications</li>
                      <li>Update room configurations</li>
                    </ul>
                  </Col>
                  <Col md="4">
                    <div className="mb-2">
                      <strong>Manage Property:</strong>
                    </div>
                    <ul className="small text-muted">
                      <li>View property details</li>
                      <li>Update property information</li>
                      <li>Manage property settings</li>
                    </ul>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ProviderProperties;